<template>
  <div>
    <div class="btn_agendar">
      <Button :icon="Plus" plain class="btn" @click="openNewSchedule">
        Novo agendamento
      </Button>
      <div v-if="showBusinessClientFilter" class="client-filter-wrap">
        <span class="client-filter-label">Empresa</span>
        <el-select
          v-model="selectedClient"
          placeholder="Selecione a empresa"
          class="client-filter app-select"
          @change="handleClientFilterChange"
        >
          <el-option label="Todos" :value="null" />
          <el-option
            v-for="client in effectiveClients"
            :key="client"
            :label="client"
            :value="client"
          />
        </el-select>
      </div>
    </div>

    <div class="filters-row">
      <div class="filter-group">
        <span class="filter-label">Cidade</span>
        <el-select
          v-model="selectedCity"
          placeholder="Todas"
          clearable
          :clear-icon="Close"
          class="filter-select app-select"
          @change="handleFilterChange"
        >
          <el-option
            v-for="(label, key) in CITY_LABELS"
            :key="key"
            :label="label"
            :value="key"
          />
        </el-select>
      </div>

      <div class="filter-group">
        <span class="filter-label">Status</span>
        <el-select
          v-model="selectedStatus"
          multiple
          collapse-tags
          collapse-tags-tooltip
          placeholder="Selecione"
          clearable
          :clear-icon="Close"
          class="filter-select app-select"
          @change="handleFilterChange"
        >
          <el-option
            v-for="(label, key) in STATUS_LABELS"
            :key="key"
            :label="label"
            :value="key"
          />
        </el-select>
      </div>

      <div class="filter-group">
        <span class="filter-label">Período</span>
        <el-select
          :model-value="activeShortcut"
          placeholder="Selecione"
          class="filter-select app-select"
          @change="applyDateShortcut"
        >
          <el-option label="Hoje" value="today" />
          <el-option label="Amanhã" value="tomorrow" />
          <el-option label="Próximos 7 dias" value="next7" />
          <el-option label="Próximos 30 dias" value="next30" />
        </el-select>
      </div>

      <div class="filter-group">
        <span class="filter-label">Data</span>
        <VDatePicker
          :key="`range-${pickerResetKey}`"
          v-model="dateRange"
          mode="date"
          is-range
          :locale="'pt-BR'"
          class="vc-picker vc-picker--filter"
        >
          <template #default="{ togglePopover, inputValue }">
            <div class="vc-input-wrap" @click="togglePopover">
              <button
                type="button"
                class="vc-input-trigger"
                aria-label="Selecionar data ou intervalo"
                @click.stop="togglePopover"
              >
                <el-icon class="vc-input-icon"><Calendar /></el-icon>
              </button>
              <input
                class="vc-input"
                :value="formatDateRangeDisplay(inputValue)"
                readonly
                placeholder="Selecione"
              />
              <button
                v-if="dateRange.start || dateRange.end"
                type="button"
                class="vc-input-clear"
                aria-label="Limpar data"
                @click.stop="clearDateRange"
              >
                ×
              </button>
            </div>
          </template>
        </VDatePicker>
      </div>

      <div class="filter-group">
        <span class="filter-label">&nbsp;</span>
        <button type="button" class="clear-filters-btn" @click="clearAllFilters">
          Limpar filtros
        </button>
      </div>
    </div>

    <Card v-if="showCard" @close="closeCard('agendamento')" title="Agendamento" width="min(640px, 92vw)">
      <Form :business-clients="effectiveClients" @submit="handleSubmit" />
    </Card>

    <Table ref="tableWrapper" :data="tableData" :columns="tableColumns">
      <template #expand="{ row }">
        <ScheduleReceiptDetails :schedule="row" :cache="receiptCache" @cache-update="saveReceiptCache" />
      </template>
      <template #header-date>
        <span
          class="sortable-header"
          role="button"
          tabindex="0"
          @click="toggleSortOrder"
          @keydown.enter="toggleSortOrder"
        >
          Data
          <el-icon class="sort-icon"><component :is="sortIcon" /></el-icon>
        </span>
      </template>
    </Table>

    <div v-if="!loading && total === 0" class="empty-state">
      Nenhum agendamento encontrado com os filtros atuais.
    </div>

    <div v-if="total > 0" class="pagination-row">
      <span class="pagination-summary">{{ count }} de {{ total }} agendamentos</span>
      <el-select
        :model-value="pageSize"
        placement="top-end"
        class="page-size-select app-select"
        @change="handlePageSizeChange"
      >
        <el-option
          v-for="size in [10, 20, 50, 100]"
          :key="size"
          :label="`${size}/página`"
          :value="size"
        />
      </el-select>
      <el-pagination
        background
        layout="prev, pager, next"
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import Card from "../Component/Card.vue";
import Form from "../Schedule/Form.vue";
import Button from "../Component/Button.vue";
import Table from "../Component/Table.vue";
import ScheduleReceiptDetails from "../Schedule/ScheduleReceiptDetails.vue";
import axios from "axios";
import { config } from "../../config";
import { ElLoading as Loading, ElMessage } from "element-plus";
import { Plus, Calendar, ArrowUp, ArrowDown, Close } from "@element-plus/icons-vue";
import { loadBusinessClients } from "../../services/businessClients";
import { clientViewState } from "../../utils/clientView";

const CITY_LABELS = {
  sp: "São Paulo",
  rj: "Rio de Janeiro",
  bh: "Belo Horizonte",
  df: "Distrito Federal",
};

const STATUS_LABELS = {
  scheduled: "Agendado",
  onair: "No ar",
  finished: "Finalizado",
  error: "Erro",
  canceled: "Cancelado",
};
const DEFAULT_STATUSES = ["scheduled", "onair", "finished"];

function humanizedStatus(status) {
  if (!status) {
    return '';
  }

  return STATUS_LABELS[status] || status;
}

function toISODateOnly(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function extractErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return "Verifique os filtros informados.";
  }
  return detail || "Não foi possível carregar os agendamentos.";
}

export default {
  name: "ListSchedules",
  components: { Table, Card, Button, Form, ScheduleReceiptDetails },
  data() {
    return {
      showCard: false,
      tableData: [],
      receiptCache: {},
      loading: false,
      loadingInstance: null,
      businessClient: null,
      canChooseBusinessClient: false,
      availableClients: [],
      clientViewState,
      selectedClient: null,
      selectedCity: null,
      selectedStatus: [...DEFAULT_STATUSES],
      sortOrder: "desc",
      dateRange: { start: null, end: null },
      dateChangeQueued: false,
      pickerResetKey: 0,
      page: 1,
      pageSize: 20,
      count: 0,
      total: 0,
      totalPages: 0,
      CITY_LABELS,
      STATUS_LABELS,
      tableColumns: [
        { prop: "title", label: "Título" },
        { prop: "description", label: "Descrição" },
        { prop: "cities", label: "Localidade" },
        { prop: "hour", label: "Hora" },
        { prop: "date", label: "Data" },
        { prop: "humanStatus", label: "Status" },
      ],
    };
  },
  computed: {
    Plus: () => Plus,
    Calendar: () => Calendar,
    Close: () => Close,
    sortIcon() {
      return this.sortOrder === "asc" ? ArrowUp : ArrowDown;
    },
    showBusinessClientFilter() {
      return this.effectiveClients.length > 1;
    },
    effectiveClients() {
      return this.clientViewState.selectedClient
        ? [this.clientViewState.selectedClient]
        : this.availableClients;
    },
    effectiveBusinessClient() {
      return this.effectiveClients.length === 1 ? this.effectiveClients[0] : null;
    },
    activeShortcut() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = toISODateOnly(today);

      const from = toISODateOnly(this.dateRange?.start);
      const to = toISODateOnly(this.dateRange?.end);
      if (!from || !to) return null;

      if (from === todayStr && to === todayStr) return "today";

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = toISODateOnly(tomorrow);
      if (from === tomorrowStr && to === tomorrowStr) return "tomorrow";

      if (from === todayStr) {
        const in7 = new Date(today);
        in7.setDate(in7.getDate() + 7);
        if (to === toISODateOnly(in7)) return "next7";

        const in30 = new Date(today);
        in30.setDate(in30.getDate() + 30);
        if (to === toISODateOnly(in30)) return "next30";
      }

      return null;
    },
  },
  watch: {
    "clientViewState.selectedClient"() {
      this.receiptCache = {};
      this.applyClientScope();
      this.handleFilterChange();
    },
    dateRange: {
      deep: true,
      handler() {
        this.scheduleFilterChange();
      },
    },
  },
  methods: {
    saveReceiptCache(scheduleId, entry) {
      this.receiptCache = { ...this.receiptCache, [scheduleId]: entry };
    },
    handleClientFilterChange() {
      this.receiptCache = {};
      this.handleFilterChange();
    },
    openNewSchedule() {
      this.showCard = true;
      document.body.style.overflow = "hidden";
    },
    handleFilterChange() {
      this.page = 1;
      this.fetchTableData();
    },
    scheduleFilterChange() {
      // Coalesce dateRange.start + dateRange.end changing in the same tick (e.g. a shortcut button) into a single fetch.
      if (this.dateChangeQueued) return;
      this.dateChangeQueued = true;
      this.$nextTick(() => {
        this.dateChangeQueued = false;
        this.handleFilterChange();
      });
    },
    clearDateRange() {
      this.dateRange = { start: null, end: null };
      this.pickerResetKey += 1;
    },
    formatDateRangeDisplay(inputValue) {
      if (!inputValue?.start) return "";
      if (!inputValue.end || inputValue.end === inputValue.start) return inputValue.start;
      return `${inputValue.start} – ${inputValue.end}`;
    },
    clearAllFilters() {
      this.selectedCity = null;
      this.selectedStatus = [...DEFAULT_STATUSES];
      this.dateRange = { start: null, end: null };
      this.pickerResetKey += 1;
      this.scheduleFilterChange();
    },
    applyDateShortcut(type) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (type === "today") {
        this.dateRange = { start: today, end: new Date(today) };
      } else if (type === "tomorrow") {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        this.dateRange = { start: tomorrow, end: new Date(tomorrow) };
      } else if (type === "next7") {
        const end = new Date(today);
        end.setDate(end.getDate() + 7);
        this.dateRange = { start: today, end };
      } else if (type === "next30") {
        const end = new Date(today);
        end.setDate(end.getDate() + 30);
        this.dateRange = { start: today, end };
      }

      this.pickerResetKey += 1;
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.handleFilterChange();
    },
    handlePageChange(newPage) {
      this.page = newPage;
      this.fetchTableData();
    },
    handlePageSizeChange(newSize) {
      this.pageSize = newSize;
      this.page = 1;
      this.fetchTableData();
    },
    buildParams() {
      const clientFilter = this.showBusinessClientFilter
        ? this.selectedClient
        : this.effectiveBusinessClient;
      const params = {
        page: this.page,
        page_size: this.pageSize,
        sort_order: this.sortOrder,
      };

      if (clientFilter) params.business_client = clientFilter;
      if (this.selectedCity) params.city = this.selectedCity;
      if (this.selectedStatus.length) params.status = this.selectedStatus.join(",");

      const from = toISODateOnly(this.dateRange?.start);
      const to = toISODateOnly(this.dateRange?.end);
      if (from && to && from === to) {
        params.start_date_on = from;
      } else {
        if (from) params.start_date_from = from;
        if (to) params.start_date_to = to;
      }

      return params;
    },
    async fetchTableData() {
      const loadingInstance = Loading.service({
        target: this.$refs.tableWrapper?.$el || this.$el,
        text: "Carregando dados...",
      });
      this.loadingInstance = loadingInstance;
      this.loading = true;
      try {
        const params = this.buildParams();
        const { data } = await axios.get(`${config.API}/v1/schedule/`, { params });
        const items = data?.items || [];
        this.tableData = items.map((item) => ({
          ...item,
          cities: item.cities
            .map((city) => CITY_LABELS[city] || city)
            .join(", "),
          date: new Date(item.start_date)
            .toLocaleString("pt-BR", { timezone: "UTC" })
            .split(", ")[0],
          hour: new Date(item.start_date)
            .toLocaleString("pt-BR", { timezone: "UTC" })
            .split(", ")[1],
          humanStatus: humanizedStatus(item.status),
        }));
        this.count = data?.count ?? items.length;
        this.total = data?.total ?? items.length;
        this.page = data?.page ?? this.page;
        this.pageSize = data?.page_size ?? this.pageSize;
        this.totalPages = data?.total_pages ?? 0;
      } catch (e) {
        console.error("Erro ao buscar dados da tabela:", e);
        ElMessage.error(extractErrorMessage(e));
        this.tableData = [];
        this.count = 0;
        this.total = 0;
        this.totalPages = 0;
      } finally {
        this.loading = false;
        loadingInstance.close();
        if (this.loadingInstance === loadingInstance) {
          this.loadingInstance = null;
        }
      }
    },
    async fetchBusinessClients() {
      try {
        this.availableClients = await loadBusinessClients();
        this.canChooseBusinessClient = this.availableClients.length > 1;
        this.businessClient = this.availableClients.length === 1
          ? this.availableClients[0]
          : null;
        this.selectedClient = this.businessClient;
      } catch (e) {
        console.error("Erro ao buscar clientes disponíveis:", e);
        ElMessage.error("Não foi possível carregar as empresas autorizadas.");
      }
    },
    applyClientScope() {
      this.businessClient = this.effectiveBusinessClient;
      this.selectedClient = this.effectiveBusinessClient;
    },
    closeCard(type) {
      if (type === "agendamento") this.showCard = false;
      if (!this.showCard) document.body.style.overflow = "";
    },
    handleSubmit() {
      this.showCard = false;
      document.body.style.overflow = "";
      this.handleFilterChange();
    },
  },
  async mounted() {
    await Promise.all([this.fetchBusinessClients(), this.fetchTableData()]);

    // Permite chegar já com o modal de novo agendamento aberto (ex.: botão
    // "Criar novo agendamento" do Dashboard) via /schedule?new=1.
    if (this.$route.query.new) {
      this.openNewSchedule();
      this.$router.replace({ query: {} });
    }
  },
  beforeUnmount() {
    this.loadingInstance?.close();
    this.loadingInstance = null;
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
@import "@/assets/css/fonts.css";

.btn_agendar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 25px;
  gap: 12px;
}

.client-filter-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Globo-Regular", sans-serif;
}

.client-filter-label {
  color: #2b4054;
  font-size: 14px;
  font-family: "Globo-Regular", sans-serif;
}

.client-filter {
  width: 260px;
}

.btn_agendar :deep(.el-button.is-plain) {
  color: #3a6d8c;
  font-weight: 600;
}

.btn_agendar :deep(.el-button.is-plain .el-icon) {
  color: #3a6d8c;
}

.filters-row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  row-gap: 14px;
  column-gap: 20px;
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  color: #2b4054;
  font-size: 14px;
  font-family: "Globo-Regular", sans-serif;
}

.app-select :deep(.el-select__wrapper) {
  min-height: 40px;
  box-shadow: 0 0 0 1px #dcdfe6 inset;
}

.app-select :deep(.el-select__wrapper.is-hovering:not(.is-focused)),
.app-select :deep(.el-select__wrapper.is-focused) {
  box-shadow: 0 0 0 1px #3a6d8c inset;
}

.app-select :deep(.el-select__selected-item) {
  color: #2b4054;
  font-family: "Globo-Regular", sans-serif;
}

.app-select :deep(.el-select__placeholder) {
  color: #2b4054;
  font-family: "Globo-Regular", sans-serif;
}

.app-select :deep(.el-select__placeholder.is-transparent) {
  color: #9aa4af;
}

.app-select :deep(.el-select__caret) {
  color: #9aa4af;
}

.filter-select {
  width: 160px;
}

.filter-select :deep(.el-select__clear) {
  color: #9aa4af;
}

.filter-select :deep(.el-select__clear:hover) {
  color: #3a6d8c;
}

.clear-filters-btn {
  height: 40px;
  padding: 0 4px;
  border: none;
  background: transparent;
  color: #6f7b87;
  font-size: 13px;
  font-family: "Globo-Regular", sans-serif;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
}

.clear-filters-btn:hover {
  color: #3a6d8c;
}

.sortable-header {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
}

.sortable-header .sort-icon {
  font-size: 13px;
  color: #6f7b87;
}

.empty-state {
  padding: 32px 0;
  text-align: center;
  color: #6f7b87;
  font-family: "Globo-Regular", sans-serif;
  font-size: 14px;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.pagination-summary {
  color: #6f7b87;
  font-size: 13px;
  font-family: "Globo-Regular", sans-serif;
}

.pagination-row :deep(.el-pagination) {
  font-family: "Globo-Regular", sans-serif;
  --el-color-primary: #3a6d8c;
  --el-pagination-hover-color: #3a6d8c;
}

.pagination-row :deep(.el-pagination.is-background .el-pager li.is-active),
.pagination-row :deep(.el-pagination.is-background .el-pager li.is-active:hover) {
  background-color: #3a6d8c !important;
  color: #fff !important;
}

.page-size-select {
  width: 110px;
}

:global(.el-select-dropdown__item) {
  font-family: "Globo-Regular", sans-serif;
}

.vc-picker--filter {
  width: 160px;
}

.vc-picker :deep(.vc-popover-content-wrapper) {
  left: 0 !important;
  transform: none !important;
  top: calc(100% + 4px) !important;
}

.vc-input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 0 12px 0 0;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.vc-input-wrap:hover,
.vc-input-wrap:focus-within {
  border-color: #3a6d8c;
}

.vc-input-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.vc-input-icon {
  color: #9aa4af;
  flex-shrink: 0;
}

.vc-input {
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  font-size: 14px;
  font-family: "Globo-Regular", sans-serif;
  color: #2b4054;
  cursor: pointer;
}

.vc-input::placeholder {
  color: #9aa4af;
  font-family: "Globo-Regular", sans-serif;
}

.vc-input-clear {
  border: none;
  background: transparent;
  color: #9aa4af;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 8px 0 4px;
}

@media (max-width: 640px) {
  .filters-row {
    align-items: stretch;
  }

  .filter-select,
  .vc-picker--filter {
    width: 100%;
  }
}
</style>
